import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// Supabase auth storage adapter (P0-SEC-01).
//
// Web:    window.localStorage — UNCHANGED behaviour; the website must keep working exactly as before.
// Native: @capacitor/preferences, which persists to app-sandboxed UserDefaults (iOS) /
//         SharedPreferences (Android). This keeps the Supabase session token OUT of the WebView's
//         localStorage / IndexedDB, satisfying the Phase-0 acceptance criterion
//         ("no Supabase token in localStorage/IndexedDB on device").
//
// HARDENING (build-host / P0-SEC follow-up): swap the native backend for a Keychain/Keystore
// secure-storage plugin (e.g. @aparajita/capacitor-secure-storage, requires Node 20+/Cap7+) so the
// token is hardware-encrypted at rest. The supabase-js storage contract below is unchanged by that
// swap — only `nativeStorage`'s three methods get re-pointed.
export interface AsyncStorageLike {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

const webStorage: AsyncStorageLike = {
  getItem: async (k) => (typeof localStorage !== 'undefined' ? localStorage.getItem(k) : null),
  setItem: async (k, v) => {
    if (typeof localStorage !== 'undefined') localStorage.setItem(k, v);
  },
  removeItem: async (k) => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(k);
  },
};

const nativeStorage: AsyncStorageLike = {
  getItem: async (k) => {
    const { value } = await Preferences.get({ key: k });
    return value ?? null;
  },
  setItem: async (k, v) => {
    await Preferences.set({ key: k, value: v });
  },
  removeItem: async (k) => {
    await Preferences.remove({ key: k });
  },
};

export const secureStorageAdapter: AsyncStorageLike = Capacitor.isNativePlatform()
  ? nativeStorage
  : webStorage;
